import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { resolveAppMountBase } from './lib/mountBase'
import { registerServiceWorker } from './lib/registerServiceWorker'
import { startConnectivityMonitor } from './lib/connectivity'
import './index.css'

// Start listening for connectivity changes before the first render, so the very
// first bootstrap request already knows whether the device has a network.
startConnectivityMonitor()

// Mark the display mode on <html> BEFORE first paint. `--cc-shell-h` (index.css)
// needs `100lvh` in an installed PWA and `100dvh` in a browser tab — the right
// viewport unit genuinely differs, and getting it wrong costs either a dead
// strip at the bottom or a composer below the fold.
//
// The `@media (display-mode: standalone)` query alone is not enough: iOS
// support for it is inconsistent, and `navigator.standalone` is the check that
// has actually proven reliable there. Both paths set the same variable, so
// whichever the browser honours, the answer matches.
try {
  const nav = window.navigator as Navigator & { standalone?: boolean }
  const standalone =
    nav.standalone === true
    || (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
  if (standalone) document.documentElement.classList.add('cc-standalone')
} catch { /* non-browser env — the media query still covers the common case */ }

const routerBase = resolveAppMountBase(typeof window !== 'undefined' ? window.location.pathname : '/')

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter basename={routerBase || undefined}>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
)

// Retire the parser-painted boot shell (index.html) now that React owns the
// screen. Fades rather than cuts, so the handover to the in-app splash doesn't
// register as a flicker. requestAnimationFrame waits for the first committed
// frame — removing it synchronously here would expose a blank gap.
requestAnimationFrame(() => {
  const boot = document.getElementById('cc-boot')
  if (!boot) return
  boot.classList.add('cc-boot-done')
  setTimeout(() => boot.remove(), 250)
})

// Mounted builds register the SW inline before the <base> tag is injected
// (Chrome throws InvalidStateError if a <base> with a different href is present
// at register-time). The dashboard itself runs without a <base>, so register here.
if (!document.querySelector('base[data-cc-mount-base]')) {
  registerServiceWorker()
}
